import {useQuery} from "@tanstack/react-query";
import {Link} from "react-router-dom";
import {
    ArrowUpRight,
    Building2,
    CheckCircle2,
    CircleDollarSign,
    FileWarning,
    Landmark,
    ReceiptText,
    RotateCcw,
    Users,
} from "lucide-react";
import {AdminTable} from "../../shared/components/AdminTable";
import {DataState} from "../../shared/components/DataState";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {useTranslation} from "../../shared/i18n";
import {formatDateTime, formatMoney, formatNumber} from "../../shared/utils/format";
import {loadPartnerDashboard} from "./api";

const Metric = ({label, value, note, icon: Icon, money = false, loading = false}) => {
    const {t} = useTranslation();
    return (
    <article className="admin-card admin-metric-card">
        <div className="admin-metric-topline">
            <span>{label}</span>
            <Icon size={17} />
        </div>
        <p className={`admin-card-value ${money ? "is-money" : ""}`}>
            {loading ? "—" : money ? formatMoney(value) : formatNumber(value)}
        </p>
        <p className="admin-card-note">{loading ? t("orgs.common.loadingData") : note}</p>
    </article>
    );
};

export const PartnerOverviewPage = () => {
    const {t} = useTranslation();
    const {data, isLoading, isError, refetch} = useQuery({
        queryKey: ["partner-dashboard"],
        queryFn: loadPartnerDashboard,
    });

    if (isError) {
        return (
            <div className="admin-page">
                <DataState
                    title={t("orgs.partner.errorTitle")}
                    copy={t("orgs.partner.errorCopy")}
                />
                <button className="admin-button admin-button-secondary" type="button" onClick={() => refetch()}>
                    <RotateCcw size={16} /> {t("orgs.common.retry")}
                </button>
            </div>
        );
    }

    const summary = data?.summary || {};
    const place = data?.scope?.place || {};
    const payouts = data?.payouts || {};
    const documents = data?.documents || {};
    const missingDocuments = documents.missing || [];

    const branchColumns = [
        {
            key: "name",
            label: t("orgs.common.branch"),
            render: (row) => (
                <Link className="admin-table-link" to={`/employers/${place.id}/branch/${row.id}`}>
                    <strong>{row.name || t("orgs.common.branchNumber", {id: row.id})}</strong>
                </Link>
            ),
        },
        {key: "staff_count", label: t("orgs.common.staff"), render: (row) => formatNumber(row.staff_count)},
        {key: "payments_count", label: t("orgs.common.purchases"), render: (row) => formatNumber(row.payments_count)},
        {key: "gross_amount", label: t("orgs.common.turnover"), render: (row) => formatMoney(row.gross_amount)},
        {key: "partner_amount", label: t("orgs.partner.payable"), render: (row) => <strong>{formatMoney(row.partner_amount)}</strong>},
        {
            key: "status",
            label: t("orgs.common.status"),
            render: (row) => (
                <StatusBadge tone={row.is_active && row.is_shown ? "success" : "warning"}>
                    {row.is_active && row.is_shown ? t("orgs.partner.working") : t("orgs.partner.limited")}
                </StatusBadge>
            ),
        },
    ];

    const paymentColumns = [
        {key: "created_at", label: t("orgs.partner.date"), render: (row) => formatDateTime(row.created_at)},
        {key: "branch_name", label: t("orgs.common.branch")},
        {key: "user_name", label: t("orgs.partner.user"), render: (row) => row.user_name || row.user_phone || "—"},
        {key: "sum", label: t("orgs.partner.amount"), render: (row) => <strong>{formatMoney(row.sum)}</strong>},
        {key: "pay_type", label: t("orgs.partner.method"), render: (row) => row.pay_type || row.bill_type || "—"},
        {key: "status", label: t("orgs.common.status"), render: (row) => <StatusBadge tone="success">{row.status}</StatusBadge>},
    ];

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <p className="admin-eyebrow">{t("orgs.partner.eyebrow")}</p>
                    <h1 className="admin-page-title">{place.name || t("orgs.partner.fallbackTitle")}</h1>
                    <p className="admin-page-copy">
                        {t("orgs.partner.copy")}
                    </p>
                </div>
                {place.id && (
                    <Link className="admin-button admin-button-secondary" to={`/employers/${place.id}`}>
                        <Building2 size={16} /> {t("orgs.partner.card")} <ArrowUpRight size={14} />
                    </Link>
                )}
            </header>

            <section className="admin-grid cols-4">
                <Metric label={t("orgs.common.turnover")} value={summary.gross_amount} note={t("orgs.partner.successful")} icon={CircleDollarSign} money loading={isLoading} />
                <Metric label={t("orgs.partner.payable")} value={summary.partner_amount} note={t("orgs.partner.commissionNote", {amount: formatMoney(summary.commission_amount)})} icon={Landmark} money loading={isLoading} />
                <Metric label={t("orgs.common.purchases")} value={summary.successful_payments_count} note={t("orgs.partner.branchNote", {count: summary.branches_count || 0})} icon={ReceiptText} loading={isLoading} />
                <Metric label={t("orgs.common.staff")} value={summary.staff_count} note={t("orgs.partner.pendingBills", {count: summary.pending_remote_bills || 0})} icon={Users} loading={isLoading} />
            </section>

            <section className="admin-grid cols-2">
                <article className="admin-card">
                    <div className="admin-chart-header">
                        <div>
                            <p className="admin-card-title"><Landmark size={14} /> {t("orgs.partner.settlements")}</p>
                            <p className="admin-card-note">{t("orgs.partner.settlementsNote")}</p>
                        </div>
                        <Link className="admin-inline-link" to="/settlements">{t("orgs.partner.open")}</Link>
                    </div>
                    <div className="admin-ledger-rows">
                        <div><span>{t("orgs.partner.drafts")}</span><strong>{formatNumber(payouts.draft_count)}</strong></div>
                        <div><span>{t("orgs.partner.approved")}</span><strong>{formatNumber(payouts.approved_count)}</strong></div>
                        <div><span>{t("orgs.partner.paid")}</span><strong>{formatNumber(payouts.paid_count)}</strong></div>
                        <div><span>{t("orgs.partner.payableAmount")}</span><strong>{formatMoney(payouts.payable_amount)}</strong></div>
                    </div>
                </article>

                <article className="admin-card">
                    <div className="admin-chart-header">
                        <div>
                            <p className="admin-card-title"><FileWarning size={14} /> {t("orgs.partner.documentsTitle")}</p>
                            <p className="admin-card-note">{t("orgs.partner.documentsNote")}</p>
                        </div>
                        <StatusBadge tone={missingDocuments.length ? "warning" : "success"}>
                            {missingDocuments.length ? t("orgs.partner.missing", {count: missingDocuments.length}) : t("orgs.partner.ready")}
                        </StatusBadge>
                    </div>
                    <div className="admin-ledger-rows">
                        {["bin", "account", "agreement"].map((key) => {
                            const present = documents[`has_${key}`];
                            return (
                                <div key={key}>
                                    <span>{t(`orgs.partner.documents.${key}`)}</span>
                                    <strong>{present ? <><CheckCircle2 size={14} /> {t("orgs.partner.present")}</> : t("orgs.partner.empty")}</strong>
                                </div>
                            );
                        })}
                    </div>
                </article>
            </section>

            <section className="admin-card">
                <div className="admin-chart-header">
                    <div>
                        <p className="admin-card-title"><Building2 size={14} /> {t("orgs.common.branches")}</p>
                        <p className="admin-card-note">{t("orgs.partner.branchesNote")}</p>
                    </div>
                </div>
                <AdminTable columns={branchColumns} rows={data?.branches || []} loading={isLoading} emptyText={t("orgs.partner.branchesEmpty")} />
            </section>

            <section className="admin-card">
                <div className="admin-chart-header">
                    <div>
                        <p className="admin-card-title"><ReceiptText size={14} /> {t("orgs.partner.recent")}</p>
                        <p className="admin-card-note">{t("orgs.partner.recentNote")}</p>
                    </div>
                    <Link className="admin-inline-link" to="/reports">{t("orgs.partner.allOperations")}</Link>
                </div>
                <AdminTable columns={paymentColumns} rows={data?.recent_payments || []} loading={isLoading} emptyText={t("orgs.partner.paymentsEmpty")} />
            </section>
        </div>
    );
};
